"use client";

import { useEffect, useMemo, useRef, useState } from "react";

// Optional: uncomment only if you install framer-motion
// import { AnimatePresence, motion } from "framer-motion";

export type GreetingsLoaderProps = {
  showForMs?: number; // Total time overlay stays before exiting
  stepMs?: number; // Interval for rotating greetings
  onFinish?: () => void; // Called after overlay slid up and unmounted
  greetings?: string[]; // Custom greetings list
  watermark?: string; // Background text, defaults to "ADARSH"
  useFramer?: boolean; // Use framer-motion for greeting transitions
};

const DEFAULT_GREETINGS = [
  // South Asia
  "नमस्ते",
  "ਸਤਿ ਸ੍ਰੀ ਅਕਾਲ ਜੀ",
  // Global
  "Hello",
  // East Asia
  "こんにちは",
  "你好",
  "안녕하세요",
  // Middle East & others
  "مرحبا",
  // Europe
  "Bonjour",
  "Hola",
  "Ciao",
  "Olá",
  "Hej",
  "Hallo",
  "Merhaba",
  "Здраво",
  // More
  "Привет",
];

export default function GreetingsLoader({
  showForMs = 2800,
  stepMs = 500,
  onFinish,
  greetings = DEFAULT_GREETINGS,
  watermark = "ADARSH",
  useFramer = false,
}: GreetingsLoaderProps) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [exiting, setExiting] = useState(false); // triggers slide-up
  const textKey = index; // used for re-animating the text element
  const wmRef = useRef<HTMLDivElement | null>(null);
  const [wmScale, setWmScale] = useState(1);
  const prevOverflowRef = useRef<string | null>(null);

  const safeGreetings = useMemo(
    () => (greetings && greetings.length > 0 ? greetings : DEFAULT_GREETINGS),
    [greetings]
  );

  // Hide SSR placeholder on mount (do not remove to avoid hydration conflicts)
  useEffect(() => {
    const placeholder = document.getElementById("gl-ssr");
    if (placeholder) {
      placeholder.setAttribute("aria-hidden", "true");
      placeholder.setAttribute("hidden", "true");
      // Hide without removing to keep React's hydration tree intact
      (placeholder as HTMLElement).style.opacity = "0";
      (placeholder as HTMLElement).style.pointerEvents = "none";
      (placeholder as HTMLElement).style.display = "none";
    }
  }, []);

  // Lock/unlock scroll based on visibility
  useEffect(() => {
    if (visible) {
      if (prevOverflowRef.current === null) {
        prevOverflowRef.current = document.body.style.overflow;
      }
      document.body.style.overflow = "hidden";
    } else {
      if (prevOverflowRef.current !== null) {
        document.body.style.overflow = prevOverflowRef.current;
        prevOverflowRef.current = null;
      }
    }
    return () => {
      // Safety: restore on unmount if still locked
      if (prevOverflowRef.current !== null) {
        document.body.style.overflow = prevOverflowRef.current;
        prevOverflowRef.current = null;
      }
    };
  }, [visible]);

  // Rotate greetings
  useEffect(() => {
    const id = window.setInterval(
      () => {
        setIndex((i) => (i + 1) % safeGreetings.length);
      },
      Math.max(120, stepMs)
    ); // clamp to avoid 0ms
    return () => clearInterval(id);
  }, [safeGreetings.length, stepMs]);

  // Schedule exit
  useEffect(() => {
    const id = window.setTimeout(
      () => {
        const reduce =
          typeof window !== "undefined" &&
          window.matchMedia &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduce) {
          // No transitions: unmount immediately and finish
          setVisible(false);
          onFinish?.();
        } else {
          setExiting(true); // triggers slide-up, then onTransitionEnd -> visible=false
        }
      },
      Math.max(300, showForMs)
    );
    return () => clearTimeout(id);
  }, [showForMs, onFinish]);

  // Measure watermark and compute scale to fit viewport
  useEffect(() => {
    const el = wmRef.current;
    if (!el) return;

    let raf = 0;
    const measure = () => {
      if (!el) return;
      el.style.transform = "translate(-50%, -50%) scale(1)";
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const scaleX = (vw * 0.92) / Math.max(1, rect.width);
      const scaleY = (vh * 0.75) / Math.max(1, rect.height);
      const scale = Math.max(0.1, Math.min(scaleX, scaleY));
      setWmScale(scale);
    };

    const handle = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };

    handle();
    const ro = new ResizeObserver(handle);
    ro.observe(document.documentElement);
    window.addEventListener("resize", handle);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", handle);
    };
  }, [watermark]);

  // Unmount after slide-up transition ends
  const onOverlayTransitionEnd: React.TransitionEventHandler<HTMLDivElement> = (
    e
  ) => {
    if (e.target !== e.currentTarget) return;
    if (exiting && e.propertyName === "transform") {
      setVisible(false);
      onFinish?.();
    }
  };

  if (!visible) return null;

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Container styles
  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#0f0f10",
    color: "rgba(255,255,255,0.9)",
    overflow: "hidden",
    // Slide-up + fade
    transform:
      exiting && !prefersReduced ? "translateY(-100%)" : "translateY(0)",
    opacity: exiting && !prefersReduced ? 0.98 : 1,
    transition: prefersReduced
      ? "none"
      : "transform 720ms cubic-bezier(0.22, 1, 0.36, 1), opacity 720ms ease",
    willChange: "transform, opacity",
  };

  // Watermark styles (with dynamic scale)
  const watermarkStyle: React.CSSProperties = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: `translate(-50%, -50%) scale(${wmScale})`,
    transformOrigin: "center",
    pointerEvents: "none",
    userSelect: "none",
    whiteSpace: "nowrap",
    fontFamily: "serif",
    fontWeight: 600,
    fontSize: "24vmin",
    letterSpacing: "0.06em",
    lineHeight: 1,
    color: "rgba(255,255,255,0.035)",
    textAlign: "center",
  };

  // Greeting text styles
  const textStyle: React.CSSProperties = {
    position: "relative",
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji"',
    fontWeight: 700,
    fontSize: "clamp(24px, 6vw, 64px)",
    letterSpacing: "0.01em",
    textAlign: "center",
    textShadow: "0 2px 14px rgba(0,0,0,0.45)",
    padding: "0 1rem",
    color: "rgba(255,255,255,0.9)",
    animation: prefersReduced ? undefined : "glowIn 420ms ease",
  };

  return (
    <div
      style={overlayStyle}
      role="status"
      aria-label="Loading"
      aria-busy={true}
      onTransitionEnd={onOverlayTransitionEnd}
    >
      {/* Watermark */}
      <div ref={wmRef} style={watermarkStyle}>
        {watermark}
      </div>

      {/* Centered greeting text */}
      {useFramer ? (
        // Framer Motion version (uncomment imports above to use)
        // <AnimatePresence mode="wait">
        //   <motion.div
        //     key={textKey}
        //     initial={{ opacity: 0, y: 8 }}
        //     animate={{ opacity: 1, y: 0 }}
        //     exit={{ opacity: 0, y: -8 }}
        //     transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        //     style={textStyle}
        //   >
        //     {safeGreetings[textKey]}
        //   </motion.div>
        // </AnimatePresence>
        <div key={textKey} style={textStyle}>
          {safeGreetings[textKey]}
        </div>
      ) : (
        <div key={textKey} style={textStyle}>
          {safeGreetings[textKey]}
        </div>
      )}

      {/* Local keyframes and reduced motion helpers */}
      <style>{`
        @keyframes glowIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0px); }
        }
        @media (prefers-reduced-motion: reduce) {
          * {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
}
